import React, { useEffect, useState } from "react";
import { api, extractError } from "@/lib/api";
import { Link } from "react-router-dom";
import { Plus, Trash2, X, FileText, Layers } from "lucide-react";
import { formatDateIt } from "@/lib/format";

export default function SOPList() {
    const [items, setItems] = useState([]);
    const [tab, setTab] = useState("project");
    const [showNew, setShowNew] = useState(false);
    const load = async () => setItems((await api.get("/sop-templates")).data);
    useEffect(() => { load(); }, []);

    const filtered = items.filter((i) => i.type === tab);

    const remove = async (id) => {
        if (!window.confirm("Eliminare il template SOP? I progetti/programmi già creati da esso non verranno modificati.")) return;
        await api.delete(`/sop-templates/${id}`); load();
    };

    return (
        <div className="p-6" data-testid="sop-page">
            <div className="flex items-baseline justify-between mb-4">
                <div>
                    <h1 className="font-display text-2xl font-semibold tracking-tight text-neutral-900">SOP</h1>
                    <div className="text-sm text-neutral-500">Template di progetto e di programma riutilizzabili</div>
                </div>
                <button onClick={() => setShowNew(true)} data-testid="new-sop-btn"
                    className="inline-flex items-center gap-1.5 bg-neutral-900 text-white text-sm font-medium px-3 py-1.5 rounded-md hover:bg-neutral-800">
                    <Plus size={14} /> Nuovo template
                </button>
            </div>

            <div className="flex gap-2 mb-4 border-b border-neutral-200">
                {[
                    ["project", "Template Progetto", FileText],
                    ["program", "Template Programma", Layers],
                ].map(([k, l, I]) => (
                    <button key={k} onClick={() => setTab(k)} data-testid={`sop-tab-${k}`}
                        className={`px-3 py-1.5 text-sm border-b-2 -mb-px inline-flex items-center gap-1.5 ${tab === k ? "border-[#FFE500] text-neutral-900 font-semibold" : "border-transparent text-neutral-500 hover:text-neutral-700"}`}>
                        <I size={13} /> {l}
                    </button>
                ))}
            </div>

            {filtered.length === 0 ? (
                <div className="bg-white border border-neutral-200 rounded-md p-10 text-center" data-testid="sop-empty">
                    <div className="font-display text-neutral-800 font-medium">Nessun template</div>
                    <div className="text-sm text-neutral-500 mt-1">
                        {tab === "project"
                            ? "Crea un template di progetto per riempire nuovi progetti con programmi e task predefiniti."
                            : "Crea un template di programma per riempire nuovi programmi con task predefinite."}
                    </div>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {filtered.map((s) => (
                        <div key={s.id} className="bg-white border border-neutral-200 rounded-md p-4" data-testid={`sop-card-${s.id}`}>
                            <div className="flex items-start justify-between gap-2">
                                <Link to={`/sop/${s.id}`} className="font-display font-semibold text-neutral-900 hover:underline truncate">{s.name}</Link>
                                <button onClick={() => remove(s.id)} className="text-neutral-400 hover:text-red-600" data-testid={`sop-delete-${s.id}`}><Trash2 size={13} /></button>
                            </div>
                            {s.description && <div className="text-sm text-neutral-600 mt-1 line-clamp-2">{s.description}</div>}
                            <div className="text-xs text-neutral-400 mt-3">Aggiornato: {formatDateIt(s.updated_at)}</div>
                        </div>
                    ))}
                </div>
            )}

            {showNew && <NewSOPModal defaultType={tab} onClose={() => setShowNew(false)} onCreated={() => { setShowNew(false); load(); }} />}
        </div>
    );
}

function NewSOPModal({ defaultType, onClose, onCreated }) {
    const [form, setForm] = useState({ name: "", description: "", type: defaultType });
    const [err, setErr] = useState(""); const [busy, setBusy] = useState(false);
    const submit = async (e) => {
        e.preventDefault(); setBusy(true); setErr("");
        try { await api.post("/sop-templates", form); onCreated(); }
        catch (e) { setErr(extractError(e)); } finally { setBusy(false); }
    };
    return (
        <div className="fixed inset-0 bg-neutral-900/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
            <form onClick={(e) => e.stopPropagation()} onSubmit={submit} className="bg-white rounded-md shadow-lg w-full max-w-md" data-testid="new-sop-modal">
                <div className="flex items-center justify-between px-5 py-3 border-b border-neutral-200">
                    <div className="font-display font-semibold">Nuovo template SOP</div>
                    <button type="button" onClick={onClose}><X size={16} /></button>
                </div>
                <div className="p-5 space-y-3">
                    <label className="block">
                        <span className="text-xs font-medium text-neutral-600">Nome*</span>
                        <input required data-testid="sop-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 w-full px-2 py-1.5 border border-neutral-300 rounded text-sm" />
                    </label>
                    <label className="block">
                        <span className="text-xs font-medium text-neutral-600">Descrizione</span>
                        <textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1 w-full px-2 py-1.5 border border-neutral-300 rounded text-sm" />
                    </label>
                    <label className="block">
                        <span className="text-xs font-medium text-neutral-600">Tipo</span>
                        <select data-testid="sop-type" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="mt-1 w-full px-2 py-1.5 border border-neutral-300 rounded text-sm">
                            <option value="project">Template Progetto</option>
                            <option value="program">Template Programma</option>
                        </select>
                    </label>
                </div>
                {err && <div className="mx-5 mb-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">{err}</div>}
                <div className="px-5 py-3 border-t border-neutral-100 flex justify-end gap-2 bg-neutral-50">
                    <button type="button" onClick={onClose} className="px-3 py-1.5 text-sm rounded border border-neutral-300 bg-white">Annulla</button>
                    <button type="submit" disabled={busy} data-testid="save-sop-btn" className="px-3 py-1.5 text-sm rounded bg-neutral-900 text-white hover:bg-neutral-800 disabled:opacity-60">{busy ? "Salvataggio…" : "Crea"}</button>
                </div>
            </form>
        </div>
    );
}
